if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 990px)');
        this.isVerticalLayout = this.dataset.desktopLayout === 'thumbnail_slider';
        this.isProcessing = false; // Prevent rapid click issues
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Mobile: Adjust slider height to match active item (prevents blank space)
        this.initMobileHeightFix();
      }

      initMobileHeightFix() {
        const slider = this.elements.viewer?.querySelector('.product__media-list');
        if (!slider) return;

        // Store reference for cleanup
        this.mobileSlider = slider;

        // Handle viewport changes (mobile <-> desktop)
        this.mql.addEventListener('change', this.handleViewportChange.bind(this));

        // Apply fix if currently on mobile
        if (!this.mql.matches) {
          this.applyMobileHeightStyles();
        }
      }

      handleViewportChange(event) {
        if (event.matches) {
          // Changed to desktop - remove mobile styles
          this.removeMobileHeightStyles();
        } else {
          // Changed to mobile - apply mobile styles
          this.applyMobileHeightStyles();
          const activeItem = this.mobileSlider?.querySelector('.product__media-item.is-active') ||
                            this.mobileSlider?.querySelector('.product__media-item');
          if (activeItem) this.adjustMobileSliderHeight(activeItem);
        }
      }

      applyMobileHeightStyles() {
        if (!this.mobileSlider) return;
        this.mobileSlider.style.transition = 'height 0.3s ease';
        this.mobileSlider.style.overflowY = 'hidden';

        // Set initial height
        const activeItem = this.mobileSlider.querySelector('.product__media-item.is-active') ||
                          this.mobileSlider.querySelector('.product__media-item');
        if (activeItem) {
          this.adjustMobileSliderHeight(activeItem);
        }
      }

      removeMobileHeightStyles() {
        if (!this.mobileSlider) return;
        this.mobileSlider.style.transition = '';
        this.mobileSlider.style.overflowY = '';
        this.mobileSlider.style.height = '';
      }

      adjustMobileSliderHeight(activeItem) {
        if (this.mql.matches || !this.mobileSlider || !activeItem) return;

        // Wait for images to load before measuring height
        const img = activeItem.querySelector('img');
        if (img && !img.complete) {
          img.addEventListener('load', () => {
            this.mobileSlider.style.height = activeItem.offsetHeight + 'px';
          }, { once: true });
        }
        this.mobileSlider.style.height = activeItem.offsetHeight + 'px';
      }

      onSlideChanged(event) {
        if (!event.detail?.currentElement?.dataset?.mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        // Adjust height on swipe
        this.adjustMobileSliderHeight(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        // Prevent rapid clicks from corrupting gallery state
        if (this.isProcessing) return;
        this.isProcessing = true;
        setTimeout(() => { this.isProcessing = false; }, 300);

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          this.isProcessing = false;
          return;
        }

        // Get all media items
        const allMediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');

        // For vertical stacked layout on desktop, scroll to the image instead of hiding others
        if (this.mql.matches && this.isVerticalLayout) {
          // Remove active class from all items
          allMediaItems.forEach((element) => {
            element.classList.remove('is-active');
          });
          // Add active class to selected item
          activeMedia.classList.add('is-active');

          // Smooth scroll to the image
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'start' });

          // Don't do any other positioning
          this.preventStickyHeader();
          return;
        }

        // Mobile behavior - instant switch
        allMediaItems.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // Adjust height on thumbnail click
        this.adjustMobileSliderHeight(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Only scroll horizontally on mobile or when thumbnails are horizontal
          if (!this.mql.matches || !this.isVerticalLayout) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Don't auto-scroll to image on vertical layout (desktop)
          if (this.isVerticalLayout && this.mql.matches) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // Handle vertical thumbnail scrolling for thumbnail_slider layout
        if (this.isVerticalLayout && this.mql.matches) {
          const scrollContainer = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
          if (!scrollContainer) return;

          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.clientHeight;
          const currentTop = scrollContainer.scrollTop;
          const currentBottom = currentTop + scrollContainer.clientHeight;

          if (thumbnailTop < currentTop) {
            scrollContainer.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          } else if (thumbnailBottom > currentBottom) {
            scrollContainer.scrollTo({
              top: thumbnailBottom - scrollContainer.clientHeight,
              behavior: 'smooth',
            });
          }
          return;
        }

        // Horizontal thumbnail scrolling (mobile/other layouts)
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
